import { useCallback, useEffect, useState } from 'react';
import {
  ClockCircleOutlined,
  ReloadOutlined,
  ShoppingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, Input, Modal, Row, Select, Space, Statistic, Table, Tag, message } from 'antd';
import { post } from '../../api';

const PAGE_SIZE = 20;

const STATUS = {
  '-1': { text: '已取消', color: 'default' },
  0: { text: '待付款', color: 'orange' },
  1: { text: '待商家采购', color: 'blue' },
  2: { text: '待平台发货', color: 'geekblue' },
  3: { text: '待收货', color: 'purple' },
  4: { text: '已收货', color: 'cyan' },
  5: { text: '已完成', color: 'green' },
  6: { text: '退款中', color: 'red' },
};

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: '0', label: '待付款' },
  { value: '1', label: '待商家采购' },
  { value: '2', label: '待平台发货' },
  { value: '3', label: '待收货' },
  { value: '4', label: '已收货' },
  { value: '5', label: '已完成' },
  { value: '6', label: '退款中' },
];

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatTime = (value) => (value ? String(value).replace('T', ' ').slice(0, 19) : '-');

export default function Orders() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [allTotal, setAllTotal] = useState(0);
  const [pendingShip, setPendingShip] = useState(0);
  const [refunding, setRefunding] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (targetPage = page) => {
      setLoading(true);
      try {
        const rsp = await post('/admin/order/list', {
          page: targetPage,
          size: PAGE_SIZE,
          status: status === '' ? undefined : Number(status),
          keyword: keyword || undefined,
        });
        if (rsp.code) {
          message.error(rsp.msg || '加载失败');
          return;
        }
        setList(rsp.data.list || []);
        setTotal(rsp.data.total || 0);
        setAllTotal(rsp.data.allTotal || 0);
        setPendingShip(rsp.data.pendingShip || 0);
        setRefunding(rsp.data.refunding || 0);
      } catch (e) {
        message.error('网络异常，请稍后再试');
      } finally {
        setLoading(false);
      }
    },
    [page, status, keyword],
  );

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, keyword]);

  const ship = async (ids) => {
    setBusy(true);
    try {
      const rsp = await post('/admin/order/ship', { ids });
      if (rsp.code) {
        message.error(rsp.msg || '发货失败');
        return;
      }
      message.success(`已发货 ${rsp.data.shipped} 笔`);
      setSelectedKeys([]);
      await load();
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setBusy(false);
    }
  };

  const refund = (record, agree) => {
    Modal.confirm({
      title: agree ? '同意退款？' : '驳回退款？',
      content: agree
        ? `将退回买家 ${money(record.totalAmount)}，已采购的订单同时退回商家采购款。`
        : '订单将恢复到申请退款前的状态。',
      onOk: async () => {
        const rsp = await post('/admin/order/refund', { id: record.id, agree: agree ? 1 : 0 });
        if (rsp.code) {
          message.error(rsp.msg || '操作失败');
          return;
        }
        message.success(agree ? '已退款' : '已驳回');
        await load();
      },
    });
  };

  const stats = [
    { title: '待平台发货', value: pendingShip, icon: <ClockCircleOutlined />, color: '#fa8c16' },
    { title: '待审核退款', value: refunding, icon: <SyncOutlined />, color: '#ff4d4f' },
    { title: '订单总数', value: allTotal, icon: <ShoppingOutlined />, color: '#1677ff' },
  ];

  const columns = [
    { title: '订单号', dataIndex: 'orderNo', width: 175 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value) => {
        const item = STATUS[value] || { text: value, color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    { title: '店铺', dataIndex: 'storeName', width: 120, ellipsis: true, render: (value) => value || '-' },
    { title: '收货人', dataIndex: 'contact', width: 90, ellipsis: true, render: (value) => value || '-' },
    { title: '实付金额', dataIndex: 'totalAmount', width: 105, render: money },
    {
      title: '毛利 / 佣金',
      dataIndex: 'profit',
      width: 115,
      render: (value, record) => (
        <div>
          <div>{money(value)}</div>
          <div style={{ color: '#8c8c8c', fontSize: 12 }}>
            {record.profitStatus === 1 ? '已释放' : '未释放'}
          </div>
        </div>
      ),
    },
    { title: '采购额', dataIndex: 'pushAmount', width: 100, render: money },
    {
      title: '售后',
      dataIndex: 'returnStatus',
      width: 85,
      render: (value) => {
        const item = {
          1: { text: '申请中', color: 'orange' },
          2: { text: '已退款', color: 'red' },
          3: { text: '已驳回', color: 'default' },
        }[value];
        return item ? <Tag color={item.color}>{item.text}</Tag> : '-';
      },
    },
    { title: '下单时间', dataIndex: 'createTime', width: 145, render: formatTime },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 120,
      render: (_, record) => (
        <Space>
          {record.status === 2 && record.purchStatus === 1 ? (
            <Button size="small" type="primary" onClick={() => ship([record.id])} loading={busy}>
              发货
            </Button>
          ) : null}
          {record.status === 6 && record.returnStatus === 1 ? (
            <>
              <Button size="small" type="primary" onClick={() => refund(record, true)}>
                退款
              </Button>
              <Button size="small" danger onClick={() => refund(record, false)}>
                驳回
              </Button>
            </>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {stats.map((item) => (
          <Col xs={24} sm={8} key={item.title}>
            <Card size="small" loading={loading}>
              <Statistic
                title={item.title}
                value={item.value ?? 0}
                prefix={<span style={{ color: item.color }}>{item.icon}</span>}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <div className="list-toolbar">
        <div className="list-title">订单管理</div>
        <div className="list-actions">
          <Select
            style={{ width: 160 }}
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            options={STATUS_OPTIONS}
          />
          <Input.Search
            allowClear
            placeholder="搜索订单号 / 店铺名"
            style={{ width: 260 }}
            onSearch={(value) => {
              setKeyword(value.trim());
              setPage(1);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => load()}>
            刷新
          </Button>
          <Button
            type="primary"
            disabled={!selectedKeys.length}
            loading={busy}
            onClick={() => ship(selectedKeys)}
          >
            批量发货
          </Button>
        </div>
      </div>

      <Table
        rowKey="id"
        bordered
        className="list-table"
        loading={loading}
        columns={columns}
        dataSource={list}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
          getCheckboxProps: (record) => ({ disabled: record.status !== 2 }),
        }}
        scroll={{ x: 1155, y: 'calc(100vh - 380px)' }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (count) => `共 ${count} 条`,
          onChange: setPage,
        }}
      />
    </>
  );
}
